/**
 * Customer Store - Zustand
 * State management for Individual Taxpayer Customer Management
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  IndividualCustomer,
  CustomerFilters,
  CustomerStatus,
  CreateCustomerInput,
  UpdateCustomerInput,
  SavedFilterView,
  CustomerAssignmentInput,
  CustomerAssignmentEvent,
} from '@/types/customer'
import { getCustomerService } from '@/services/customers'

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface CustomerState {
  // Data
  customers: IndividualCustomer[]
  selectedCustomer: IndividualCustomer | null
  isLoading: boolean
  error: string | null

  // Filters
  filters: CustomerFilters
  searchQuery: string
  sortBy: 'name' | 'lastUpdated' | 'createdDate' | 'pan'
  sortOrder: 'asc' | 'desc'

  // Pagination
  page: number
  pageSize: number

  // Selection for bulk actions
  selectedIds: string[]

  // UI State
  isFilterPanelOpen: boolean
  viewMode: 'table' | 'card'

  // Saved Views
  savedViews: SavedFilterView[]
}

interface CustomerActions {
  // Loading
  loadCustomers: () => Promise<void>

  // CRUD Operations
  addCustomer: (input: CreateCustomerInput) => Promise<IndividualCustomer>
  updateCustomer: (id: string, updates: UpdateCustomerInput) => Promise<void>
  deleteCustomer: (id: string, reason?: string) => Promise<void>
  archiveCustomer: (id: string) => Promise<void>
  restoreCustomer: (id: string) => Promise<void>

  // Selection
  setSelectedCustomer: (id: string | null) => void
  toggleSelectCustomer: (id: string) => void
  selectAllCustomers: (ids: string[]) => void
  clearSelection: () => void

  // Filters
  setFilters: (filters: Partial<CustomerFilters>) => void
  clearFilters: () => void
  setSearchQuery: (query: string) => void
  setSortBy: (sortBy: CustomerState['sortBy']) => void
  setSortOrder: (order: CustomerState['sortOrder']) => void

  // Saved Views
  saveCurrentView: (name: string) => void
  applySavedView: (id: string) => void
  deleteSavedView: (id: string) => void

  // Pagination
  setPage: (page: number) => void
  setPageSize: (size: number) => void

  // UI State
  toggleFilterPanel: () => void
  setViewMode: (mode: 'table' | 'card') => void

  // Computed/Derived
  getFilteredCustomers: () => IndividualCustomer[]
  getCustomerById: (id: string) => IndividualCustomer | undefined
  getPaginatedCustomers: () => {
    items: IndividualCustomer[]
    total: number
    page: number
    pageSize: number
    totalPages: number
  }

  // Bulk Actions
  bulkArchive: (ids: string[]) => Promise<void>
  bulkDelete: (ids: string[]) => Promise<void>
  bulkAssignCA: (ids: string[], caId: string) => Promise<void>

  // Governed Customer Assignment
  assignCustomer: (customerId: string, input: CustomerAssignmentInput) => Promise<void>
  loadAssignmentHistory: (customerId: string) => Promise<CustomerAssignmentEvent[]>

  // Reset
  resetStore: () => void
}

type CustomerStore = CustomerState & CustomerActions

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: CustomerState = {
  customers: [],
  selectedCustomer: null,
  isLoading: false,
  error: null,
  filters: {
    status: 'all',
    assignedCA: 'all',
    occupation: 'all',
    incomeRange: 'all',
    hasPendingAssessments: false,
    hasPendingDocuments: false,
    state: 'all',
  },
  searchQuery: '',
  sortBy: 'name',
  sortOrder: 'asc',
  page: 1,
  pageSize: 20,
  selectedIds: [],
  isFilterPanelOpen: false,
  viewMode: 'table',
  savedViews: [],
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useCustomerStore = create<CustomerStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ========================================
        // Loading
        // ========================================

        loadCustomers: async () => {
          set({ isLoading: true, error: null })
          try {
            const customers = await getCustomerService().list()
            set({ customers, isLoading: false })
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load customers',
            })
          }
        },

        // ========================================
        // CRUD Operations
        // ========================================

        addCustomer: async (input: CreateCustomerInput) => {
          set({ isLoading: true, error: null })
          try {
            const newCustomer = await getCustomerService().create(input)
            set((state) => ({
              customers: [newCustomer, ...state.customers],
              isLoading: false,
            }))
            return newCustomer
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create customer'
            set({ isLoading: false, error: message })
            throw err
          }
        },

        updateCustomer: async (id: string, updates: UpdateCustomerInput) => {
          set({ isLoading: true, error: null })
          try {
            const patch = await getCustomerService().update(id, updates)
            set((state) => ({
              customers: state.customers.map((c) =>
                c.id === id ? { ...c, ...patch } : c
              ),
              selectedCustomer:
                state.selectedCustomer?.id === id
                  ? { ...state.selectedCustomer, ...patch }
                  : state.selectedCustomer,
              isLoading: false,
            }))
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update customer'
            set({ isLoading: false, error: message })
            throw err
          }
        },

        deleteCustomer: async (id: string, reason = 'User requested deletion') => {
          set({ isLoading: true, error: null })

          await getCustomerService().remove(id, reason)

          set((state) => ({
            customers: state.customers.filter((c) => c.id !== id),
            selectedCustomer:
              state.selectedCustomer?.id === id ? null : state.selectedCustomer,
            selectedIds: state.selectedIds.filter((i) => i !== id),
            isLoading: false,
          }))
        },

        archiveCustomer: async (id: string) => {
          await get().updateCustomer(id, { status: 'archived' })
        },

        restoreCustomer: async (id: string) => {
          await get().updateCustomer(id, { status: 'active' })
        },

        // ========================================
        // Selection
        // ========================================

        setSelectedCustomer: (id: string | null) => {
          const customer = id ? get().customers.find((c) => c.id === id) : null
          set({ selectedCustomer: customer || null })
        },

        toggleSelectCustomer: (id: string) => {
          set((state) => ({
            selectedIds: state.selectedIds.includes(id)
              ? state.selectedIds.filter((i) => i !== id)
              : [...state.selectedIds, id],
          }))
        },

        selectAllCustomers: (ids: string[]) => {
          set({ selectedIds: ids })
        },

        clearSelection: () => {
          set({ selectedIds: [] })
        },

        // ========================================
        // Filters
        // ========================================

        setFilters: (newFilters: Partial<CustomerFilters>) => {
          set((state) => ({
            filters: { ...state.filters, ...newFilters },
            page: 1, // Reset to first page when filters change
          }))
        },

        clearFilters: () => {
          set({
            filters: initialState.filters,
            searchQuery: '',
            page: 1,
          })
        },

        setSearchQuery: (query: string) => {
          set({ searchQuery: query, page: 1 })
        },

        setSortBy: (sortBy: CustomerState['sortBy']) => {
          set({ sortBy })
        },

        setSortOrder: (order: CustomerState['sortOrder']) => {
          set({ sortOrder: order })
        },

        // ========================================
        // Saved Views
        // ========================================

        saveCurrentView: (name: string) => {
          const newView: SavedFilterView = {
            id: `view-${Date.now()}`,
            name,
            filters: { ...get().filters },
            createdAt: new Date().toISOString(),
          }
          set((state) => ({
            savedViews: [...state.savedViews, newView],
          }))
        },

        applySavedView: (id: string) => {
          const view = get().savedViews.find((v) => v.id === id)
          if (view) {
            set({
              filters: { ...view.filters },
              page: 1,
            })
          }
        },

        deleteSavedView: (id: string) => {
          set((state) => ({
            savedViews: state.savedViews.filter((v) => v.id !== id),
          }))
        },

        // ========================================
        // Pagination
        // ========================================

        setPage: (page: number) => {
          set({ page })
        },

        setPageSize: (pageSize: number) => {
          set({ pageSize, page: 1 })
        },

        // ========================================
        // UI State
        // ========================================

        toggleFilterPanel: () => {
          set((state) => ({ isFilterPanelOpen: !state.isFilterPanelOpen }))
        },

        setViewMode: (mode: 'table' | 'card') => {
          set({ viewMode: mode })
        },

        // ========================================
        // Computed/Derived
        // ========================================

        getFilteredCustomers: () => {
          const { customers, filters, searchQuery, sortBy, sortOrder } = get()

          let filtered = [...customers]

          // Apply search
          if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim()
            filtered = filtered.filter(
              (c) =>
                c.fullName.toLowerCase().includes(query) ||
                c.pan.toLowerCase().includes(query) ||
                c.emailPrimary.toLowerCase().includes(query) ||
                c.mobilePrimary.includes(query) ||
                (c.aadhaar && c.aadhaar.includes(query))
            )
          }

          // Apply status filter
          if (filters.status && filters.status !== 'all') {
            filtered = filtered.filter((c) => c.status === filters.status)
          }

          // Apply CA filter
          if (filters.assignedCA && filters.assignedCA !== 'all') {
            filtered = filtered.filter((c) => c.assignedCA === filters.assignedCA)
          }

          // Apply occupation filter
          if (filters.occupation && filters.occupation !== 'all') {
            filtered = filtered.filter((c) => c.occupation === filters.occupation)
          }

          // Apply income range filter
          if (filters.incomeRange && filters.incomeRange !== 'all') {
            filtered = filtered.filter((c) => c.annualIncomeRange === filters.incomeRange)
          }

          // Apply state filter
          if (filters.state && filters.state !== 'all') {
            filtered = filtered.filter(
              (c) => c.residentialAddress.state === filters.state
            )
          }

          // Apply pending assessments filter
          if (filters.hasPendingAssessments) {
            filtered = filtered.filter((c) => c.pendingAssessments > 0)
          }

          // Apply sorting
          filtered.sort((a, b) => {
            let comparison = 0

            switch (sortBy) {
              case 'name':
                comparison = a.fullName.localeCompare(b.fullName)
                break
              case 'lastUpdated':
                comparison = new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
                break
              case 'createdDate':
                comparison = new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
                break
              case 'pan':
                comparison = a.pan.localeCompare(b.pan)
                break
              default:
                comparison = a.fullName.localeCompare(b.fullName)
            }

            return sortOrder === 'asc' ? comparison : -comparison
          })

          return filtered
        },

        getCustomerById: (id: string) => {
          return get().customers.find((c) => c.id === id)
        },

        getPaginatedCustomers: () => {
          const { page, pageSize } = get()
          const filtered = get().getFilteredCustomers()
          const total = filtered.length
          const totalPages = Math.ceil(total / pageSize)
          const startIndex = (page - 1) * pageSize
          const items = filtered.slice(startIndex, startIndex + pageSize)

          return {
            items,
            total,
            page,
            pageSize,
            totalPages,
          }
        },

        // ========================================
        // Bulk Actions
        // ========================================

        bulkArchive: async (ids: string[]) => {
          set({ isLoading: true })

          await getCustomerService().bulkArchive(ids)

          set((state) => ({
            customers: state.customers.map((c) =>
              ids.includes(c.id)
                ? { ...c, status: 'archived' as CustomerStatus, lastUpdated: new Date().toISOString() }
                : c
            ),
            selectedIds: [],
            isLoading: false,
          }))
        },

        bulkDelete: async (ids: string[]) => {
          set({ isLoading: true })

          await getCustomerService().bulkDelete(ids)

          set((state) => ({
            customers: state.customers.filter((c) => !ids.includes(c.id)),
            selectedIds: [],
            isLoading: false,
          }))
        },

        bulkAssignCA: async (ids: string[], caId: string) => {
          set({ isLoading: true })

          const { caName } = await getCustomerService().bulkAssignCA(ids, caId)

          set((state) => ({
            customers: state.customers.map((c) =>
              ids.includes(c.id)
                ? {
                    ...c,
                    assignedCA: caId,
                    assignedCAName: caName,
                    lastUpdated: new Date().toISOString(),
                  }
                : c
            ),
            selectedIds: [],
            isLoading: false,
          }))
        },

        assignCustomer: async (customerId: string, input: CustomerAssignmentInput) => {
          set({ isLoading: true, error: null })
          try {
            await getCustomerService().assignCustomer(customerId, input)
            set((state) => ({
              customers: state.customers.map((c) =>
                String(c.id) === String(customerId)
                  ? {
                      ...c,
                      ...(input.assignedToUserId !== undefined && {
                        assignedCA: input.assignedToUserId,
                        assignedCAName: input.assignedToName,
                      }),
                      ...(input.reviewerUserId !== undefined && {
                        reviewerUserId: input.reviewerUserId,
                        reviewerName: input.reviewerName,
                      }),
                      ...(input.researcherUserId !== undefined && {
                        researcherUserId: input.researcherUserId,
                        researcherName: input.researcherName,
                      }),
                      lastUpdated: new Date().toISOString(),
                    }
                  : c
              ),
              isLoading: false,
            }))
          } catch (err) {
            set({ error: err instanceof Error ? err.message : 'Failed to assign customer', isLoading: false })
            throw err
          }
        },

        loadAssignmentHistory: async (customerId: string) => {
          return await getCustomerService().getAssignmentHistory(customerId)
        },

        // ========================================
        // Reset
        // ========================================

        resetStore: () => {
          set(initialState)
        },
      }),
      {
        name: 'taxhive-customer-store',
        partialize: (state) => ({
          // Only persist UI preferences and saved views
          viewMode: state.viewMode,
          pageSize: state.pageSize,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
          savedViews: state.savedViews,
        }),
      }
    ),
    { name: 'CustomerStore' }
  )
)

// ============================================================================
// SELECTORS (for optimized re-renders)
// ============================================================================

export const selectCustomers = (state: CustomerStore) => state.customers
export const selectSelectedCustomer = (state: CustomerStore) => state.selectedCustomer
export const selectIsLoading = (state: CustomerStore) => state.isLoading
export const selectFilters = (state: CustomerStore) => state.filters
export const selectSearchQuery = (state: CustomerStore) => state.searchQuery
export const selectPage = (state: CustomerStore) => state.page
export const selectPageSize = (state: CustomerStore) => state.pageSize
export const selectSelectedIds = (state: CustomerStore) => state.selectedIds
export const selectViewMode = (state: CustomerStore) => state.viewMode
export const selectIsFilterPanelOpen = (state: CustomerStore) => state.isFilterPanelOpen
export const selectSavedViews = (state: CustomerStore) => state.savedViews

// ============================================================================
// UTILITY HOOKS
// ============================================================================

export function useActiveFilterCount(): number {
  const filters = useCustomerStore(selectFilters)
  const searchQuery = useCustomerStore(selectSearchQuery)

  let count = 0
  if (filters.status && filters.status !== 'all') count++
  if (filters.assignedCA && filters.assignedCA !== 'all') count++
  if (filters.occupation && filters.occupation !== 'all') count++
  if (filters.incomeRange && filters.incomeRange !== 'all') count++
  if (filters.state && filters.state !== 'all') count++
  if (filters.hasPendingAssessments) count++
  if (filters.hasPendingDocuments) count++
  if (searchQuery.trim()) count++

  return count
}
